import { Box } from '@mui/material';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';
import { IMAGES, SVG } from '../../../../../constants/assetLists';
import {
  useClientsContext,
  ClientDataType,
  BranchData,
  SubBranchData,
} from '../../../../../context/clients/ClientsContextProvider';
import { useMemo } from 'react';
import { useOperationsContext } from '../../../../../context/OperationsContextProvider';

// Flattened option type for the dropdown — depth 0 = client, 1 = branch, 2 = sub-branch
type FlattenedOption = {
  id: string;
  name: string;
  isClient: boolean;
  isSubBranch: boolean;
  clientId?: string;
  clientName?: string;
  branchId?: string;
  icon: string;
  apiIcon?: string;
  depth: number;
};

function SearchField() {
  const { selectedClient, selectedLocation, setSelectedLocation, allClients } =
    useClientsContext();
  const { setAnnouncementFilterType, clearDaySummaryAnnouncementFilter } =
    useOperationsContext();
  // Flatten clients, branches, and sub-branches into a single list for the dropdown
  const flattenedOptions = useMemo(() => {
    const options: FlattenedOption[] = [];

    allClients?.forEach((client: ClientDataType) => {
      options.push({
        id: client.id,
        name: client.name,
        isClient: true,
        isSubBranch: false,
        icon: client.icon,
        apiIcon: client.apiIcon,
        depth: 0,
      });

      client.branches?.forEach((branch: BranchData) => {
        options.push({
          id: branch.id,
          name: branch.name,
          isClient: false,
          isSubBranch: false,
          clientId: client.id,
          clientName: client.name,
          icon: branch.icon,
          apiIcon: branch.apiIcon,
          depth: 1,
        });

        branch.subBranches?.forEach((subBranch: SubBranchData) => {
          options.push({
            id: subBranch.id,
            name: subBranch.name,
            isClient: false,
            isSubBranch: true,
            clientId: client.id,
            clientName: client.name,
            branchId: branch.id,
            icon: subBranch.icon,
            apiIcon: subBranch.apiIcon,
            depth: 2,
          });
        });
      });
    });

    return options;
  }, [allClients]);

  // Get current selected option from flattenedOptions
  const selectedOption = useMemo(() => {
    if (!selectedLocation) return null;
    return (
      flattenedOptions.find((opt) => opt.id === selectedLocation.id) || null
    );
  }, [selectedLocation, flattenedOptions]);

  // Display the logo of the selected node; fall back up the hierarchy if not set
  const displayLogo = useMemo(() => {
    if (selectedLocation && !selectedLocation.isClient) {
      const logo = (selectedLocation as BranchData | SubBranchData).apiIcon;
      if (logo) return logo;
    }
    return (selectedClient as any)?.icon || IMAGES.placeholder;
  }, [selectedLocation, selectedClient]);

  return (
    <div className="flex items-center justify-center gap-5">
      <div className="w-[160px] h-[76px]">
        <img
          src={displayLogo}
          alt="logo"
          className="w-full h-full object-contain"
        />
      </div>

      <Box sx={{ width: { lg: '358px', xs: '250px' } }}>
        <AppMUIAutocomplete
          options={flattenedOptions}
          getOptionLabel={(o: FlattenedOption) => o?.name ?? ''}
          variant="standard"
          label="עסק / סניף נבחר"
          value={selectedOption}
          isOptionEqualToValue={(
            option: FlattenedOption,
            value: FlattenedOption,
          ) => option?.id === value?.id}
          renderOption={(props: any, option: FlattenedOption) => {
            const { key, ...restProps } = props;
            const paddingInlineEnd = Math.min(16 + option.depth * 16, 56);
            const icon = option.isClient
              ? SVG.clientIcon
              : option.isSubBranch
                ? SVG.taskIcon
                : SVG.filterSidebarRestaurantIcon;

            return (
              <li
                key={key}
                {...restProps}
                style={{
                  paddingRight: `${paddingInlineEnd}px`,
                  borderBottom: option.isClient ? '1px solid #eee' : 'none',
                  backgroundColor:
                    option.depth === 2
                      ? '#f4f4f4'
                      : option.depth === 1
                        ? '#f9f9f9'
                        : 'white',
                }}
              >
                <div className="flex items-center gap-2">
                  <img
                    src={icon}
                    alt="icon"
                    className="w-[18px] h-[18px] object-contain"
                  />
                  <span
                    style={{
                      fontWeight: option.isClient ? 600 : 400,
                      fontSize: option.isClient ? '14px' : '13px',
                    }}
                  >
                    {option.name}
                  </span>
                </div>
              </li>
            );
          }}
          onChange={(_e: any, value: FlattenedOption | null) => {
            clearDaySummaryAnnouncementFilter();
            if (!value) return;

            if (value.isClient) {
              const client = allClients.find((c) => c.id === value.id);
              if (client) setSelectedLocation(client);
            } else if (value.isSubBranch) {
              const parentClient = allClients.find(
                (c) => c.id === value.clientId,
              );
              const subBranch = parentClient?.branches
                ?.find((b) => b.id === value.branchId)
                ?.subBranches?.find((sb) => sb.id === value.id);
              if (subBranch) setSelectedLocation(subBranch);
            } else {
              const parentClient = allClients.find(
                (c) => c.id === value.clientId,
              );
              const branch = parentClient?.branches?.find(
                (b) => b.id === value.id,
              );
              if (branch) setSelectedLocation(branch);
            }
            setAnnouncementFilterType('all'); // Reset the filter when changing the selected location
          }}
        />
      </Box>
    </div>
  );
}

export default SearchField;
